import { Link } from '@tanstack/react-router'
import type { BudgetCategory } from './types'
import { formatCurrency, stepMonth } from './format'
import { useTransactionsInRange } from '@/features/transactions/hooks'
import { formatAmount, formatDate } from '@/features/transactions/format'

interface Props {
  open: boolean
  onClose: () => void
  category: BudgetCategory | null
  year: number
  monthNumber: number
}

function pad2(n: number): string {
  return String(n).padStart(2, '0')
}

/**
 * Slide-over showing the transactions behind a category's Activity
 * number for the month. Split transactions count their split's
 * amount, mirroring how activity is rolled up. Each row deep-links
 * to /transactions?txn=<id> for recategorizing.
 */
export function ActivityDrilldown({ open, onClose, category, year, monthNumber }: Props) {
  const start = `${year}-${pad2(monthNumber)}-01`
  const next = stepMonth(year, monthNumber, 1)
  const end = `${next.year}-${pad2(next.month)}-01`
  const txns = useTransactionsInRange(start, end, open && category !== null)

  if (!category) return null

  const rows = (txns.data ?? []).flatMap((t) => {
    if (t.categoryId === category.id) return [{ t, amount: t.amount, isSplit: false }]
    const split = t.splits.find((s) => s.categoryId === category.id)
    return split ? [{ t, amount: split.amount, isSplit: true }] : []
  })
  const total = Math.round(rows.reduce((s, r) => s + r.amount, 0) * 100) / 100

  return (
    <aside
      role="dialog"
      aria-labelledby="activity-drilldown-title"
      aria-hidden={!open}
      onKeyDown={(e) => e.key === 'Escape' && onClose()}
      className={`fixed right-0 top-0 z-40 flex h-full w-full max-w-md flex-col border-l border-zinc-200 bg-white shadow-2xl transition-transform duration-200 ease-out dark:border-zinc-800 dark:bg-zinc-950 ${
        open ? 'translate-x-0' : 'pointer-events-none translate-x-full'
      }`}
    >
      <header className="flex items-center justify-between border-b border-zinc-200 px-4 py-3 dark:border-zinc-800">
        <div>
          <h2
            id="activity-drilldown-title"
            className="text-sm font-semibold uppercase tracking-wide text-zinc-500"
          >
            Activity
          </h2>
          <div className="text-lg font-semibold">{category.name}</div>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close activity panel"
          className="rounded-md p-1 text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800"
        >
          ✕
        </button>
      </header>

      <div className="flex items-center justify-between border-b border-zinc-200 px-4 py-2 text-sm dark:border-zinc-800">
        <span className="text-zinc-500">
          {rows.length} transaction{rows.length === 1 ? '' : 's'} this month
        </span>
        <span className="font-semibold tabular-nums">{formatCurrency(total)}</span>
      </div>

      <div className="flex-1 overflow-y-auto">
        {txns.isLoading && <div className="p-4 text-sm text-zinc-500">Loading…</div>}
        {txns.isSuccess && rows.length === 0 && (
          <div className="p-4 text-sm text-zinc-500">
            No transactions in this category this month.
          </div>
        )}
        <ul className="divide-y divide-zinc-100 dark:divide-zinc-900">
          {rows.map(({ t, amount, isSplit }) => (
            <li key={t.id}>
              <Link
                to="/transactions"
                search={{ txn: t.id }}
                title="Open in Transactions to recategorize"
                className="flex items-center justify-between gap-3 px-4 py-2 text-sm hover:bg-zinc-50 dark:hover:bg-zinc-900/60"
              >
                <div className="min-w-0">
                  <div className="truncate font-medium">
                    {t.payeeName ?? t.description ?? t.memo ?? '—'}
                    {isSplit && (
                      <span className="ml-1.5 text-xs font-normal text-zinc-500">(split)</span>
                    )}
                  </div>
                  <div className="text-xs text-zinc-500">
                    {formatDate(t.date)}
                    {t.memo && t.payeeName ? ` · ${t.memo}` : ''}
                  </div>
                </div>
                <span
                  className={`shrink-0 tabular-nums ${amount > 0 ? 'text-funded' : ''}`}
                >
                  {formatAmount(amount)}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </div>

      {Math.abs(total - category.activity) > 0.01 && txns.isSuccess && (
        <div className="border-t border-zinc-200 px-4 py-2 text-xs text-amber-600 dark:border-zinc-800 dark:text-amber-500">
          Listed total differs from the month's recorded activity (
          {formatCurrency(category.activity)}) — usually carried-in YNAB history where
          transactions weren't linked to this category.
        </div>
      )}
    </aside>
  )
}
